// Off-Cycle Package modal (2026-07-04; final invoices added 2026-08-12).
//
// Sends a procurement statement outside the monthly billing cycle —
// Cimon: "we need to send a summary like the monthly statement, but it
// would only have proposals attached. they need to be recorded in the
// history under the correct date, and be able to record payment on
// billing history." Since 2026-08-12 the package also carries Ready
// final invoices (get paid before the vendor ships — the Ptarmigan
// Porada case), completing the July client-payment scope: procurement
// DEP/FIN is off-cycle AND can ride summaries.
//
// The send creates a REAL billing_summaries row (summary_type
// 'off_cycle', status 'sent' directly — never a draft, so the
// one-draft-per-project index is untouched) so the whole Billing History
// machinery works unchanged: archive row under the chosen send date,
// as-sent cover PDF + frozen payload in document_sends (the deposit-ask
// fallback the wizard reads before approval mints DEPs), per-proposal
// PDF archives, and the Record payment button.
//
// Send sequence (mirrors the monthly sendPackage, minus everything
// non-procurement):
//   1. INSERT the billing_summaries row (needs the id for tags + sends)
//   2. render the off-cycle cover (buildOffCyclePackagePayload — pure)
//   3. per proposal: render PDF → zip → archive to the proposals bucket
//      + document_sends (best-effort, never blocks the send)
//   4. upload the cover to billing-summaries + document_sends with the
//      frozen payload
//   5. tag proposals: billed_on_summary_id (race-guarded .or filter),
//      status 'Sent' + sent_via 'off_cycle' (this send IS the Mark Sent
//      for Ready-for-Review proposals), sent_at backfilled only when
//      null (targeted single-column update — never a full-row save)
//   6. download the zip for Cimon to email
//
// Role gate: rendering is gated by the caller (owner/admin — the same
// gate as Mark Sent). Pure helpers live in utils.js; this component is
// orchestration + presentation only.

import React, { useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import {
  fmtMoney,
  offCycleEligibleProposals,
  buildOffCycleProposalRow,
  offCycleEligibleFinalInvoices,
  buildOffCycleInvoiceRow,
  buildOffCyclePackagePayload,
  buildOffCycleSummaryRow,
  buildProposalPayloadPure,
  proposalStoragePath,
  billingSummaryStoragePath,
  offCycleDateLabel,
  parseQty, assemblyNet, assemblyClient,
  activeProjects,
  invoiceCcFeeTot,
} from '../utils';
import {
  INK_1, INK_2, INK_3, SURFACE,
  ZONE_FUCHSIA, ZONE_GREEN, ALERT_RED_TEXT,
  HAIRLINE, HAIRLINE_SHELL, HAIRLINE_ROW, HAIRLINE_THICK_INK,
  TABULAR, SHADOW_MODAL, SCRIM, TYPE_EYEBROW,
} from '../lib/designTokens';

var labelStyle = Object.assign({}, TYPE_EYEBROW, { marginBottom: 4 });
var inputStyle = {
  width: '100%', padding: '8px 10px', border: HAIRLINE_SHELL, borderRadius: 4,
  fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', color: INK_1,
};

export default function OffCyclePackageModal(props) {
  var ctx = useAppContext();
  var projects  = ctx.projects  || [];
  var clients   = ctx.clients   || [];
  var proposals = ctx.proposals || [];
  var allItems  = ctx.items     || [];
  var setProposals = ctx.setProposals;
  var invoices  = ctx.invoices  || [];
  var setInvoices = ctx.setInvoices;
  var paymentAllocations = ctx.paymentAllocations || [];

  var ps = useState('');   var projLabel = ps[0];  var setProjLabel = ps[1];
  var ss = useState({});   var selected = ss[0];   var setSelected = ss[1];
  var si = useState({});   var selInv = si[0];     var setSelInv = si[1];
  var ds = useState(new Date().toISOString().slice(0, 10));
  var dateIso = ds[0];     var setDateIso = ds[1];
  var es = useState(null); var sentTo = es[0];     var setSentTo = es[1];   // null = untouched (auto)
  var pv = useState('');   var previewUrl = pv[0]; var setPreviewUrl = pv[1];
  var pl = useState(false); var previewBusy = pl[0]; var setPreviewBusy = pl[1];
  var sb = useState(false); var sending = sb[0];   var setSending = sb[1];
  var st = useState('');   var sendStep = st[0];   var setSendStep = st[1];
  var er = useState('');   var err = er[0];        var setErr = er[1];
  var dn = useState(false); var done = dn[0];      var setDone = dn[1];

  var project = projects.find(function(p) { return p.label === projLabel; }) || null;
  var client = project
    ? (clients.find(function(c) { return c.id === project.clientId; }) || null)
    : null;

  var eligible = useMemo(function() {
    return projLabel ? offCycleEligibleProposals(proposals, projLabel) : [];
  }, [proposals, projLabel]);

  var rows = useMemo(function() {
    return eligible.map(function(p) { return buildOffCycleProposalRow(p, allItems, project); });
  }, [eligible, allItems, project]);

  var selectedRows = rows.filter(function(r) { return selected[r.proposal.id]; });
  var depositTotal = Math.round(selectedRows.reduce(function(s, r) { return s + r.totalDeposit; }, 0) * 100) / 100;

  // Ready FINs (2026-08-12) — Rule 10 rows; settled ($0 outstanding)
  // rows have no business on a forward-looking ask document.
  var invRows = useMemo(function() {
    if (!projLabel) return [];
    return offCycleEligibleFinalInvoices(invoices, projLabel)
      .map(function(inv) { return buildOffCycleInvoiceRow(inv, paymentAllocations); })
      .filter(function(r) { return r.amount > 0.005; });
  }, [invoices, paymentAllocations, projLabel]);

  var selectedInvRows = invRows.filter(function(r) { return selInv[r.invoice.id]; });
  var invoicesTotal = Math.round(selectedInvRows.reduce(function(s, r) { return s + r.amount; }, 0) * 100) / 100;
  var packageTotal = Math.round((depositTotal + invoicesTotal) * 100) / 100;
  var selectedCount = selectedRows.length + selectedInvRows.length;

  var sentToValue = sentTo != null
    ? sentTo
    : (client && (client.financialEmail || client.primaryEmail || client.email)) || '';

  function safeFilename(s) {
    return String(s || '').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  }

  async function loadJSZip() {
    if (window.JSZip) return window.JSZip;
    return new Promise(function(resolve, reject) {
      var script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = function() { resolve(window.JSZip); };
      script.onerror = function() { reject(new Error('Could not load JSZip')); };
      document.head.appendChild(script);
    });
  }

  async function fetchPdfBytes(endpoint, payload, parseMode) {
    var res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      var errBody = await res.json().catch(function() { return {}; });
      throw new Error(errBody.error || (endpoint + ' failed'));
    }
    if (parseMode === 'json') {
      var data = await res.json();
      return Uint8Array.from(atob(data.pdf), function(c) { return c.charCodeAt(0); });
    }
    var blob = await res.blob();
    return new Uint8Array(await blob.arrayBuffer());
  }

  // Component collapse for the proposal PDF — same math as
  // BillingSummarySection.bsResolveItemsForPdf / ProposalsTab2's
  // buildProposalPayload item resolution (parentId components fold into
  // the parent's per-unit net + synthetic markup).
  function resolveItemsForPdf(ids) {
    return allItems
      .filter(function(it) { return ids.indexOf(it.id) >= 0; })
      .map(function(it) {
        var comps = allItems.filter(function(c) { return c.parentId === it.id; });
        if (comps.length === 0) return it;
        var qty = parseQty(it.qty) || 1;
        var netTotal = assemblyNet(it, allItems);
        var clientTotal = assemblyClient(it, allItems);
        var netPerUnit = netTotal / qty;
        var synthMarkup = netPerUnit > 0 ? ((clientTotal / netTotal) - 1) * 100 : 30;
        return Object.assign({}, it, {
          netPrice: Math.round(netPerUnit * 100) / 100,
          markupPct: Math.round(synthMarkup * 100) / 100,
        });
      });
  }

  function buildProposalPdfPayload(p) {
    return buildProposalPayloadPure({
      proposal: p,
      items:    resolveItemsForPdf(p.items || []),
      client:   client || {},
      project:  project || { label: projLabel },
      allItems: allItems,
    });
  }

  function coverPayload() {
    return buildOffCyclePackagePayload({
      project:     project || { label: projLabel },
      client:      client || {},
      rows:        selectedRows,
      invoiceRows: selectedInvRows,
      dateIso:     dateIso,
    });
  }

  async function previewCover() {
    if (previewBusy) return;
    setPreviewBusy(true); setErr('');
    try {
      var bytes = await fetchPdfBytes('/api/generate-billing-summary', coverPayload(), 'json');
      var blob = new Blob([bytes], { type: 'application/pdf' });
      setPreviewUrl(function(prev) { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
    } catch (e) {
      setErr('Cover preview failed: ' + (e.message || 'unknown'));
    }
    setPreviewBusy(false);
  }

  async function send() {
    if (sending || selectedCount === 0 || !dateIso || !project) return;
    setSending(true); setErr('');
    try {
      // 1. The billing_summaries row — the package's identity in Billing
      //    History. status 'sent' at insert; sent_at carries the chosen
      //    date so the archive files it correctly.
      setSendStep('Recording the package…');
      var rowInsert = buildOffCycleSummaryRow({
        project: project, client: client || {}, rows: selectedRows,
        invoiceRows: selectedInvRows, dateIso: dateIso,
      });
      var ins = await supabase.from('billing_summaries').insert(rowInsert).select().single();
      if (ins.error) {
        var msg = String(ins.error.message || '');
        if (/summary_type/i.test(msg)) {
          throw new Error('The database is missing the off-cycle column. Run the migration 20260704_billing_summaries_summary_type.sql in the Supabase Dashboard, then try again.');
        }
        throw new Error(msg || 'Could not create the package record.');
      }
      var summaryId = ins.data.id;
      var payload = coverPayload();

      var JSZip = await loadJSZip();
      var zip = new JSZip();
      var projTag = safeFilename(project.label);

      // 2. The cover.
      setSendStep('Rendering the cover…');
      var coverBytes = null;
      try {
        coverBytes = await fetchPdfBytes('/api/generate-billing-summary', payload, 'json');
        zip.file('00_' + dateIso + '_' + projTag + '_offcycle_statement.pdf', coverBytes);
      } catch (e) { console.warn('off-cycle cover PDF failed:', e); }

      // 2b. Each final invoice — rendered from the FIN's own FROZEN
      //     line_items snapshot (never live items), exactly the monthly
      //     sendPackage's procurement-PDF block. Filenames use the same
      //     10+ slot so a package zip diff-matches a monthly delivery.
      for (var k = 0; k < selectedInvRows.length; k++) {
        var finRow = selectedInvRows[k];
        var fin = finRow.invoice;
        setSendStep('Rendering invoice ' + (k + 1) + ' of ' + selectedInvRows.length + '…');
        try {
          var snapshot = fin.lineItems || fin.line_items || [];
          if (snapshot.length === 0) continue;  // nothing to render
          var finProductTotal = parseFloat(fin.productTotal) || 0;
          var finSubtotal     = parseFloat(fin.subtotal)     || 0;
          var finFeesTotal    = parseFloat(fin.feesTotal)    || Math.round((finSubtotal - finProductTotal) * 100) / 100;
          var finFeeFreight      = parseFloat(fin.feeFreightTot)      || 0;
          var finFeeCrating      = parseFloat(fin.feeCratingTot)      || 0;
          var finFeeInstallation = parseFloat(fin.feeInstallationTot) || 0;
          var finFeeTariff       = parseFloat(fin.feeTariffTot)       || 0;
          var finFeeSalesTax     = parseFloat(fin.feeSalesTaxTot)     || 0;
          var finFeeCustom       = Array.isArray(fin.feeCustom) ? fin.feeCustom : [];
          var finHasBreakdown    = finFeeFreight + finFeeCrating + finFeeInstallation + finFeeTariff + finFeeSalesTax > 0
            || finFeeCustom.length > 0;
          var finPayload = {
            invoiceRef:      fin.invoiceRef || fin.invoice_ref || '',
            invoiceType:     fin.invoiceType || 'final',
            projectLabel:    fin.project || fin.project_label || '',
            projectDisplay:  (project && (project.displayName || project.label)) || projLabel,
            billToName:      fin.clientName || fin.client_name || ((client && client.name) || ''),
            billToAddr:      fin.billToAddr || '',
            siteAddr:        fin.siteAddr || '',
            lineItems:       snapshot,
            productTotal:    finProductTotal,
            feeFreightTot:      finFeeFreight,
            feeCratingTot:      finFeeCrating,
            feeInstallationTot: finFeeInstallation,
            feeTariffTot:       finFeeTariff,
            feeSalesTaxTot:     finFeeSalesTax,
            feeCustom: finHasBreakdown
              ? finFeeCustom
              : (finFeesTotal > 0 ? [{ label: 'Fees & Sales Tax', amount: finFeesTotal }] : []),
            feesLumped: !finHasBreakdown && finFeesTotal > 0,
            // Card fee — one source, invoiceCcFeeTot in utils.js.
            feeCcProcessingTot: invoiceCcFeeTot(fin, {
              subtotal: finSubtotal, productTotal: finProductTotal,
              feesTotal: finFeesTotal,
            }),
            subtotal:        finSubtotal,
            depositTotal:    parseFloat(fin.amountDue) || parseFloat(fin.amount) || 0,
            balanceRemaining: 0,
            notes:           fin.notes || '',
            date:            (fin.createdAt || fin.created_at || new Date().toISOString()).slice(0, 10),
          };
          var finBytes = await fetchPdfBytes('/api/generate-deposit-invoice', finPayload, 'blob');
          zip.file(String(10 + k).padStart(2, '0') + '_final_' + safeFilename(fin.invoiceRef || fin.invoice_ref || 'inv') + '.pdf', finBytes);
        } catch (e) { console.warn('final invoice PDF failed:', e); }
      }

      // 3. Each proposal — zip + archive (best-effort, mirrors the
      //    monthly sendPackage's proposal loop).
      var idx = 0;
      for (var i = 0; i < selectedRows.length; i++) {
        var p = selectedRows[i].proposal;
        setSendStep('Rendering proposal ' + (i + 1) + ' of ' + selectedRows.length + '…');
        try {
          var propPayload = buildProposalPdfPayload(p);
          var bytes = await fetchPdfBytes('/api/generate-proposal', propPayload, 'blob');
          zip.file(String(20 + idx).padStart(2, '0') + '_proposal_' + safeFilename(p.proposal_ref || p.id || 'p') + '.pdf', bytes);
          idx += 1;
          try {
            if (ctx.recordDocumentSend) {
              var pPath = proposalStoragePath(p.id, new Date());
              var pBlob = new Blob([bytes], { type: 'application/pdf' });
              var pUp = await supabase.storage
                .from('proposals')
                .upload(pPath.replace(/^proposals\//, ''), pBlob, {
                  contentType: 'application/pdf', upsert: false,
                });
              if (pUp.error) console.warn('proposal PDF archive upload failed:', pUp.error.message);
              await ctx.recordDocumentSend({
                documentType: 'proposal',
                documentId:   p.id,
                payload:      propPayload,
                storagePath:  pUp.error ? '' : pPath,
                sentTo:       sentToValue,
              });
            }
          } catch (archErr) {
            console.warn('proposal document_sends write failed:', archErr && archErr.message);
          }
        } catch (e) { console.warn('proposal PDF failed:', e); }
      }

      // 4. Archive the cover + freeze the payload. The frozen
      //    proposalsThisMonth manifest is what resolveBillingPackage's
      //    deposit-ask fallback reads while the proposals await approval.
      setSendStep('Archiving the as-sent package…');
      try {
        if (coverBytes && ctx.recordDocumentSend) {
          var storagePath = billingSummaryStoragePath(summaryId, new Date());
          var cBlob = new Blob([coverBytes], { type: 'application/pdf' });
          var up = await supabase.storage
            .from('billing-summaries')
            .upload(storagePath.replace(/^billing-summaries\//, ''), cBlob, {
              contentType: 'application/pdf', upsert: false,
            });
          if (up.error) console.warn('off-cycle cover upload failed:', up.error.message);
          await ctx.recordDocumentSend({
            documentType: 'billing_summary',
            documentId:   summaryId,
            payload:      payload,
            storagePath:  up.error ? '' : storagePath,
            sentTo:       sentToValue,
          });
        }
      } catch (sendErr) {
        console.warn('document_sends write failed:', sendErr && sendErr.message);
      }

      // 5. Tag + mark the proposals sent. Race-guarded tag (only rows
      //    still untagged or already ours), then a targeted sent_at
      //    backfill for rows that had never been marked sent. Skipped
      //    when the package carries no proposals — `.in('id', [])`
      //    would be a PostgREST parse error, not a no-op.
      var ids = selectedRows.map(function(r) { return r.proposal.id; });
      var sentAtIso = dateIso + 'T12:00:00.000Z';
      if (ids.length > 0) {
        setSendStep('Marking proposals sent…');
        var tag = await supabase.from('proposals')
          .update({
            billed_on_summary_id: summaryId,
            status: 'Sent',
            sent_via: 'off_cycle',
            sent_off_cycle_at: sentAtIso,
          })
          .in('id', ids)
          .or('billed_on_summary_id.is.null,billed_on_summary_id.eq.' + summaryId)
          .select('id');
        if (tag.error) throw new Error('Could not tag proposals: ' + tag.error.message);
        var taggedIds = (tag.data || []).map(function(t) { return t.id; });
        var lost = ids.filter(function(id) { return taggedIds.indexOf(id) < 0; });
        if (lost.length > 0) {
          console.warn('Some proposals were already tagged to another package:', lost);
        }
        await supabase.from('proposals')
          .update({ sent_at: sentAtIso })
          .in('id', taggedIds)
          .is('sent_at', null);
        if (setProposals) {
          setProposals(function(prev) {
            return prev.map(function(p) {
              if (taggedIds.indexOf(p.id) < 0) return p;
              return Object.assign({}, p, {
                billedOnSummaryId: summaryId,
                status: 'Sent',
                sentVia: 'off_cycle',
                sent_at: p.sent_at || sentAtIso,
              });
            });
          });
        }
      }

      // 5b. Tag + mark the final invoices sent (2026-08-12) — the same
      //     three moves the monthly summary's sent-cascade makes:
      //     race-guarded billed_on_summary_id tag, ready→sent flip
      //     (this send IS the FIN's delivery event), and a targeted
      //     sent_at backfill for already-sent rows missing a timestamp.
      var finIds = selectedInvRows.map(function(r) { return r.invoice.id; });
      if (finIds.length > 0) {
        setSendStep('Marking invoices sent…');
        var invTag = await supabase.from('invoices')
          .update({ billed_on_summary_id: summaryId })
          .in('id', finIds)
          .or('billed_on_summary_id.is.null,billed_on_summary_id.eq.' + summaryId)
          .select('id');
        if (invTag.error) throw new Error('Could not tag invoices: ' + invTag.error.message);
        var finTaggedIds = (invTag.data || []).map(function(t) { return t.id; });
        var finLost = finIds.filter(function(id) { return finTaggedIds.indexOf(id) < 0; });
        if (finLost.length > 0) {
          console.warn('Some invoices were already tagged to another package:', finLost);
        }
        var flippedIds = [];
        if (finTaggedIds.length > 0) {
          var flip = await supabase.from('invoices')
            .update({ status: 'sent', sent_at: sentAtIso })
            .in('id', finTaggedIds)
            .eq('status', 'ready')
            .select('id');
          if (flip.error) console.warn('FIN ready→sent flip failed:', flip.error.message);
          else flippedIds = (flip.data || []).map(function(t) { return t.id; });
          await supabase.from('invoices')
            .update({ sent_at: sentAtIso })
            .in('id', finTaggedIds)
            .is('sent_at', null);
        }
        if (setInvoices) {
          setInvoices(function(prev) {
            return prev.map(function(inv) {
              if (finTaggedIds.indexOf(inv.id) < 0) return inv;
              var next = Object.assign({}, inv, { billedOnSummaryId: summaryId });
              if (flippedIds.indexOf(inv.id) >= 0) {
                next.status = 'sent';
                next.sentAt = sentAtIso;
              } else if (!next.sentAt && !next.sent_at) {
                next.sentAt = sentAtIso;
              }
              return next;
            });
          });
        }
      }

      // 6. The deliverable zip.
      setSendStep('Building the zip…');
      var zipBlob = await zip.generateAsync({ type: 'blob' });
      var url = URL.createObjectURL(zipBlob);
      var a = document.createElement('a');
      a.href = url;
      a.download = dateIso + '_' + projTag + '_offcycle_package.zip';
      a.click();
      URL.revokeObjectURL(url);

      setDone(true);
    } catch (e) {
      setErr(e.message || 'Send failed.');
    }
    setSendStep('');
    setSending(false);
  }

  var ghostBtn = {
    padding: '5px 10px', border: HAIRLINE_SHELL, borderRadius: 4, background: SURFACE,
    color: INK_1, fontSize: 11, fontFamily: 'inherit', cursor: 'pointer',
  };
  var primaryBtn = function(enabled) {
    return {
      padding: '7px 16px', border: HAIRLINE_THICK_INK, borderRadius: 6,
      background: INK_1, color: SURFACE, opacity: enabled ? 1 : 0.4,
      fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
      cursor: enabled ? 'pointer' : 'not-allowed',
    };
  };

  var canSend = !sending && !done && selectedCount > 0 && !!dateIso && !!project;

  return (
    <div style={{ position: 'fixed', inset: 0, background: SCRIM, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: previewUrl ? 1080 : 620, maxWidth: '96vw', maxHeight: '92vh', background: SURFACE, borderRadius: 6, boxShadow: SHADOW_MODAL, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '16px 20px', borderBottom: HAIRLINE_SHELL }}>
          <div>
            <div style={{ fontSize: 14, color: INK_1, fontWeight: 500 }}>Send Off-Cycle Package</div>
            <div style={{ fontSize: 11, color: INK_3, marginTop: 2 }}>
              Proposals and final invoices, outside the monthly billing cycle
            </div>
          </div>
          <button style={{ border: 'none', background: 'transparent', color: INK_3, fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
            onClick={props.onClose} title="Close">×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px', overflowY: 'auto', flex: 1, display: 'flex', gap: 20 }}>
          {previewUrl && (
            <div style={{ flex: '0 0 46%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={Object.assign({}, TYPE_EYEBROW, { marginBottom: 6 })}>Cover preview</div>
              <iframe title="Off-cycle cover preview" src={previewUrl}
                style={{ flex: 1, minHeight: 420, width: '100%', border: HAIRLINE, borderRadius: 4 }} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {done ? (
              <div data-testid="offcycle-success">
                <div style={{ fontSize: 13, color: ZONE_GREEN, fontWeight: 500, marginBottom: 8 }}>Package sent ✓</div>
                <div style={{ fontSize: 12, color: INK_2, lineHeight: 1.6 }}>
                  The zip just downloaded — email it to the client. The package is
                  filed in <span style={{ fontWeight: 500 }}>Billing History</span> under {offCycleDateLabel(dateIso)},
                  and that's where you'll record the payment when it arrives
                  (for a proposal deposit, approve the proposal first).
                </div>
              </div>
            ) : (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <div style={labelStyle}>Project</div>
                  <select value={projLabel}
                    onChange={function(e) { setProjLabel(e.target.value); setSelected({}); setSelInv({}); setPreviewUrl(function(prev) { if (prev) URL.revokeObjectURL(prev); return ''; }); }}
                    style={Object.assign({}, inputStyle, { background: SURFACE })}>
                    <option value="">— Pick a project —</option>
                    {activeProjects(projects, projLabel).map(function(p) {
                      return <option key={p.label} value={p.label}>{p.displayName || p.label}</option>;
                    })}
                  </select>
                </div>

                {projLabel && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={labelStyle}>Proposals on this package</div>
                    {rows.length === 0 ? (
                      <div style={{ padding: '12px 12px', border: HAIRLINE_SHELL, borderRadius: 4, fontSize: 11, color: INK_3, lineHeight: 1.5 }}>
                        No proposals are ready to send off-cycle for this project.
                        A proposal shows here when it's marked <span style={{ fontWeight: 500 }}>Ready for Review</span>,
                        or was already sent off-cycle but never packaged. Proposals
                        waiting on the monthly billing summary stay in that flow.
                      </div>
                    ) : (
                      <div style={{ border: HAIRLINE_SHELL, borderRadius: 4, overflow: 'hidden' }}>
                        {rows.map(function(r, i) {
                          var p = r.proposal;
                          var checked = !!selected[p.id];
                          return (
                            <label key={p.id} style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                              borderTop: i === 0 ? 'none' : HAIRLINE_ROW, background: SURFACE, cursor: 'pointer',
                              boxShadow: checked ? 'inset 2px 0 0 ' + ZONE_FUCHSIA : 'none',
                            }}>
                              <input type="checkbox" checked={checked}
                                onChange={function() {
                                  setSelected(function(prev) {
                                    var n = Object.assign({}, prev);
                                    n[p.id] = !n[p.id];
                                    return n;
                                  });
                                }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, color: INK_1 }}>{r.name || r.ref || 'Proposal'}</div>
                                <div style={{ fontSize: 10, color: INK_3, marginTop: 1 }}>
                                  {r.ref}{p.status === 'Ready for Review' ? ' · will be marked Sent on send' : ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', fontFeatureSettings: TABULAR }}>
                                <div style={{ fontSize: 12, color: INK_1 }}>{fmtMoney(r.totalDeposit)}</div>
                                <div style={{ fontSize: 9.5, color: INK_3 }}>deposit · {fmtMoney(r.totalClient)} total</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {projLabel && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={labelStyle}>Final invoices on this package</div>
                    {invRows.length === 0 ? (
                      <div style={{ padding: '12px 12px', border: HAIRLINE_SHELL, borderRadius: 4, fontSize: 11, color: INK_3, lineHeight: 1.5 }}>
                        No final invoices are ready for this project. A final
                        invoice shows here after <span style={{ fontWeight: 500 }}>Mark Ready</span>,
                        while a balance is outstanding.
                      </div>
                    ) : (
                      <div style={{ border: HAIRLINE_SHELL, borderRadius: 4, overflow: 'hidden' }}>
                        {invRows.map(function(r, i) {
                          var inv = r.invoice;
                          var checked = !!selInv[inv.id];
                          var st = String(inv.status || '').toLowerCase();
                          var subline = r.ref
                            + (st === 'ready' ? ' · will be marked Sent on send' : '')
                            + (r.paid > 0.005 ? ' · ' + fmtMoney(r.paid) + ' already paid' : '');
                          return (
                            <label key={inv.id} style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                              borderTop: i === 0 ? 'none' : HAIRLINE_ROW, background: SURFACE, cursor: 'pointer',
                              boxShadow: checked ? 'inset 2px 0 0 ' + ZONE_FUCHSIA : 'none',
                            }}>
                              <input type="checkbox" checked={checked}
                                onChange={function() {
                                  setSelInv(function(prev) {
                                    var n = Object.assign({}, prev);
                                    n[inv.id] = !n[inv.id];
                                    return n;
                                  });
                                }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, color: INK_1 }}>{r.name || r.ref || 'Final Invoice'}</div>
                                <div style={{ fontSize: 10, color: INK_3, marginTop: 1 }}>{subline}</div>
                              </div>
                              <div style={{ textAlign: 'right', fontFeatureSettings: TABULAR }}>
                                <div style={{ fontSize: 12, color: INK_1 }}>{fmtMoney(r.amount)}</div>
                                <div style={{ fontSize: 9.5, color: INK_3 }}>balance due</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <div style={labelStyle}>Send date</div>
                    <input type="date" value={dateIso} onChange={function(e) { setDateIso(e.target.value); }} style={inputStyle} />
                    <div style={{ fontSize: 10, color: INK_3, marginTop: 3 }}>Billing History files the package under this date.</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Send to</div>
                    <input type="text" value={sentToValue} onChange={function(e) { setSentTo(e.target.value); }}
                      placeholder="client@example.com" style={inputStyle} />
                    <div style={{ fontSize: 10, color: INK_3, marginTop: 3 }}>Recorded on the send history.</div>
                  </div>
                </div>

                {selectedCount > 0 && (
                  <div style={{ borderTop: HAIRLINE_ROW, padding: '8px 0' }}>
                    {selectedRows.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: INK_1, paddingBottom: selectedInvRows.length > 0 ? 4 : 0 }}>
                        <span>{selectedRows.length} proposal{selectedRows.length === 1 ? '' : 's'} · deposits requested</span>
                        <span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(depositTotal)}</span>
                      </div>
                    )}
                    {selectedInvRows.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: INK_1 }}>
                        <span>{selectedInvRows.length} final invoice{selectedInvRows.length === 1 ? '' : 's'} · balances due</span>
                        <span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(invoicesTotal)}</span>
                      </div>
                    )}
                    {selectedRows.length > 0 && selectedInvRows.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 500, color: INK_1, marginTop: 6, paddingTop: 6, borderTop: HAIRLINE_ROW }}>
                        <span>Package total</span>
                        <span style={{ fontFeatureSettings: TABULAR }}>{fmtMoney(packageTotal)}</span>
                      </div>
                    )}
                  </div>
                )}

                {err && (
                  <div style={{ marginTop: 10, padding: '8px 10px', border: '0.5px solid ' + ALERT_RED_TEXT, borderRadius: 4, fontSize: 11, color: ALERT_RED_TEXT, background: SURFACE }}>{err}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: HAIRLINE_SHELL }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!done && (
              <button style={ghostBtn} disabled={previewBusy || selectedCount === 0}
                onClick={previewCover}>
                {previewBusy ? 'Rendering…' : 'Preview cover'}
              </button>
            )}
            {sendStep && <span style={{ fontSize: 11, color: INK_3 }}>{sendStep}</span>}
          </div>
          {done ? (
            <button style={primaryBtn(true)} onClick={props.onClose}>Close</button>
          ) : (
            <button data-testid="offcycle-send" style={primaryBtn(canSend)} disabled={!canSend} onClick={send}>
              {sending ? 'Sending…' : 'Send package'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
